import React from 'react';
import type { RecordCardScene } from '../../types';
import inline from './inline-editor.module.css';

function EditableText({
  value,
  className,
  onChange,
  placeholder,
  multiline = false,
}: {
  value: string;
  className?: string;
  onChange: (v: string) => void;
  placeholder?: string;
  multiline?: boolean;
}) {
  const handleBlur = (e: React.FocusEvent<HTMLElement>) => {
    onChange(e.currentTarget.textContent?.trim() ?? '');
  };

  if (multiline) {
    return (
      <div
        className={className}
        contentEditable
        suppressContentEditableWarning
        onBlur={handleBlur}
        data-placeholder={placeholder}
      >
        {value || ''}
      </div>
    );
  }

  return (
    <span
      className={className}
      contentEditable
      suppressContentEditableWarning
      onBlur={handleBlur}
    >
      {value || placeholder || ''}
    </span>
  );
}

interface InlineRecordCardEditorProps {
  item: RecordCardScene;
  editMode: boolean;
  onChange: (item: RecordCardScene) => void;
}

function linesToList(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

function listToLines(items: string[] | undefined): string {
  return (items ?? []).join('\n');
}

export const InlineRecordCardEditor: React.FC<InlineRecordCardEditorProps> = ({
  item,
  editMode,
  onChange,
}) => {
  if (!editMode) {
    return (
      <div className={inline.gameOverReadonly}>
        <span className={inline.slText}>{item.title || '…'}</span>
        {item.subtitle ? <span className={inline.slText}>{item.subtitle}</span> : null}
        {item.summary ? (
          <span className={`${inline.slText} ${inline.gameOverText}`}>{item.summary}</span>
        ) : null}
        {item.stamp?.text ? (
          <span className={inline.slText}>
            [{item.stamp.text}
            {item.stamp.sub ? ` · ${item.stamp.sub}` : ''}]
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className={inline.gameOverEditor}>
      <EditableText
        className={inline.gameOverHeading}
        value={item.title}
        placeholder="Nom"
        onChange={(title) => onChange({ ...item, title })}
      />
      <EditableText
        className={inline.slText}
        value={item.subtitle ?? ''}
        placeholder="Sous-titre (dite …)"
        onChange={(subtitle) => onChange({ ...item, subtitle: subtitle || undefined })}
      />
      <EditableText
        className={inline.gameOverText}
        value={item.summary ?? ''}
        placeholder="Résumé"
        multiline
        onChange={(summary) => onChange({ ...item, summary: summary || undefined })}
      />
      <div className={inline.gameOverEditor}>
        <EditableText
          className={inline.slText}
          value={item.stamp?.text ?? ''}
          placeholder="Tampon (COFFRÉE)"
          onChange={(text) =>
            onChange({
              ...item,
              stamp: text ? { ...item.stamp, text } : undefined,
            })
          }
        />
        <EditableText
          className={inline.slText}
          value={item.stamp?.sub ?? ''}
          placeholder="Sous-tampon (MISSION 6)"
          onChange={(sub) =>
            onChange({
              ...item,
              stamp: item.stamp?.text
                ? { text: item.stamp.text, sub: sub || undefined }
                : undefined,
            })
          }
        />
      </div>
      <label className={inline.slText}>Parti avec (une ligne par item)</label>
      <textarea
        className={inline.gameOverText}
        value={listToLines(item.taken)}
        rows={3}
        onChange={(e) => onChange({ ...item, taken: linesToList(e.target.value) })}
      />
      <label className={inline.slText}>Sauvé (une ligne par item)</label>
      <textarea
        className={inline.gameOverText}
        value={listToLines(item.saved)}
        rows={3}
        onChange={(e) => onChange({ ...item, saved: linesToList(e.target.value) })}
      />
    </div>
  );
};
